import * as fabric from 'fabric'
import { convertPxToCm, isContained } from '@utils/editor-bozze'

/**
 * Utility condivise di `useFabricEditor`.
 *
 * Perche' esistono:
 * - concentrano la matematica geometrica e di containment in un modulo testabile e riusabile;
 * - separano la serializzazione Fabric dal ciclo React del hook;
 * - riducono la complessita' del file principale, lasciando nel hook solo orchestrazione/stato.
 */
export const TEXT_TYPES = ['text', 'i-text', 'textbox']
// L'area esportabile e stampabile resta 4000x4000, ma la posizioniamo al centro di un
// mondo Fabric più grande per consentire pan libero senza impattare export e vincoli.
export const PRINT_AREA_SIZE = 4000
export const FABRIC_CANVAS_SIZE = 8000
export const CANVAS_CENTER_OFFSET = (FABRIC_CANVAS_SIZE - PRINT_AREA_SIZE) / 2
export const PAN_PADDING = PRINT_AREA_SIZE
export const CANVAS_BACKGROUND_ID = 'canvas-background'
export const SPECIAL_CANVAS_OBJECT_IDS = ['area-stampa', 'quota', CANVAS_BACKGROUND_ID]
export const PREVIEW_HIDDEN_OBJECT_IDS = ['area-stampa', 'quota']
export const TEXT_CONTAINMENT_NUDGE_STEPS = [0, 1, -1, 2, -2, 3, -3, 4, -4, 6, -6]
// La preview finale resta PRINT_AREA_SIZExPRINT_AREA_SIZE per il colore principale. Se esistono altri
// articoli, aggiungiamo una strip laterale da PREVIEW_STRIP_WIDTHpx per mostrarne le miniature.
export const PREVIEW_STRIP_WIDTH = 1600
export const PREVIEW_OUTPUT_WIDTH = PRINT_AREA_SIZE + PREVIEW_STRIP_WIDTH
export const PREVIEW_OUTPUT_HEIGHT = PRINT_AREA_SIZE
export const SECONDARY_PREVIEW_MAX_SIZE = 800
export const AREA_CONTAINMENT_EPSILON = 0.01
export const ROTATION_SNAP_STEP = 10
// Snap "magnetico" sui cardinali durante la rotazione libera.
// Le due soglie implementano isteresi: entrata stretta, uscita più ampia.
export const ROTATION_MAGNETIC_SNAP_ANGLES = [0, 90, 180, 270]
export const ROTATION_MAGNETIC_SNAP_ENTRY_THRESHOLD = 4
export const ROTATION_MAGNETIC_SNAP_EXIT_THRESHOLD = 7

export const FABRIC_CUSTOM_PROPS = [
  'customId',
  'editorObjectControl',
  'hasBackground',
  'isBackground',
  'originalFill',
  'originalStroke',
  'excludeFromImage',
  '_hiddenColorsData',
  '_hiddenColorsStackIndex',
] as const

// Sovrascriviamo il metodo toObject degli oggetti base
// Questo garantisce che QUALSIASI esportazione (JSON o Object) includa queste chiavi
export const originalToObject = fabric.FabricObject.prototype.toObject

fabric.FabricObject.prototype.toObject = function (additionalProperties) {
  return originalToObject.call(this, [...FABRIC_CUSTOM_PROPS, ...additionalProperties])
}

export const TRANSFORM_PROPS = [
  'left',
  'top',
  'width',
  'height',
  'angle',
  'scaleX',
  'scaleY',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'underline',
]
export const TRANSFORM_TEXT_PROPS = [
  'text',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'fontStyle',
  'underline',
]

export const getObjectEditorMeta = (obj: any) => obj?.editorObjectControl?.editorMeta || {}

// Normalizza l'URL dell'immagine area stampa per riusarlo sia nel canvas principale
// sia nella composizione finale delle preview degli altri colori.
export const getAreaStampaImageUrl = (image?: { url?: string } | null) => {
  if (!image?.url) return null

  // 1. Gestione Development
  if (process.env.NODE_ENV === 'development') {
    return `http://localhost:3000/_next/image?url=${image.url}&w=3840&q=100`
  }

  // 2. Gestione Produzione/Staging dinamica
  try {
    // Se siamo nel browser, usiamo window.location.origin (es. https://www.sipec.com)
    if (typeof window !== 'undefined') {
      const url = new URL(image.url)
      // Sostituiamo protocollo e dominio dell'immagine con quelli correnti del browser
      return `${window.location.origin}${url.pathname}${url.search}`
    }
  } catch (error) {
    console.error("Errore nel parsing dell'URL:", error)
  }

  return image.url
}

export const applyCornerOnlyControls = (obj: fabric.Object | null | undefined) => {
  if (!obj) return

  // Manteniamo solo i corner handles per impedire stretch orizzontale/verticale
  obj.set({
    hasControls: true,
    hasBorders: true,
    // Evita che durante il resize l'oggetto attraversi lo zero e si ribalti.
    lockScalingFlip: true,
  })
  obj.setControlsVisibility({
    mt: false,
    mb: false,
    ml: false,
    mr: false,
    mtr: true, // rotazione
    tl: true,
    tr: true,
    bl: true,
    br: true,
  })
}

export const getVisualObjectDimensions = (obj: fabric.Object) => {
  obj.setCoords()
  const bounds = obj.getBoundingRect()

  return {
    width: bounds.width,
    height: bounds.height,
  }
}

export const getVisualObjectDimensionsInCm = (
  obj: fabric.Object,
  areaStampa: fabric.Object,
  larghezzaAreaStampa: number,
  altezzaAreaStampa: number
) => {
  const visualDimensions = getVisualObjectDimensions(obj)
  const { objectBounds, areaBounds } = getObjectBoundsInAreaSpace(obj, areaStampa)
  const areaWidthPx = Math.max(areaBounds.maxX - areaBounds.minX, 1)
  const areaHeightPx = Math.max(areaBounds.maxY - areaBounds.minY, 1)

  return {
    width: convertPxToCm(visualDimensions.width, areaStampa.getScaledWidth(), larghezzaAreaStampa),
    height: convertPxToCm(visualDimensions.height, areaStampa.getScaledHeight(), altezzaAreaStampa),
    // Coordinate del bounding box visuale rispetto all'angolo alto-sinistra dell'area stampa.
    left: convertPxToCm(objectBounds.minX - areaBounds.minX, areaWidthPx, larghezzaAreaStampa),
    top: convertPxToCm(objectBounds.minY - areaBounds.minY, areaHeightPx, altezzaAreaStampa),
  }
}

export const refreshTextObjectDimensions = (obj: fabric.Object, action?: string) => {
  const textObject = obj as any

  // Durante il typing lasciamo a Fabric la libertà di ricalcolare la misura
  // naturale del testo; altrimenti width/height possono restare "bloccati".
  if (obj.type === 'text' && action === 'typing') {
    obj.set({ width: undefined, height: undefined })
  }

  if (TEXT_TYPES.includes(obj.type)) {
    if (textObject.initDimensions) textObject.initDimensions()
    else if (textObject._initDimensions) textObject._initDimensions()
  }

  obj.setCoords()
}

export const getBoundsFromPoints = (points: fabric.Point[]) => {
  let minX = Number.POSITIVE_INFINITY
  let maxX = Number.NEGATIVE_INFINITY
  let minY = Number.POSITIVE_INFINITY
  let maxY = Number.NEGATIVE_INFINITY

  for (const point of points) {
    if (point.x < minX) minX = point.x
    if (point.x > maxX) maxX = point.x
    if (point.y < minY) minY = point.y
    if (point.y > maxY) maxY = point.y
  }

  return { minX, maxX, minY, maxY }
}

export const getContainmentDeltaInAreaSpace = (
  objectBounds: { minX: number; maxX: number; minY: number; maxY: number },
  areaBounds: { minX: number; maxX: number; minY: number; maxY: number }
) => {
  const objectWidth = objectBounds.maxX - objectBounds.minX
  const objectHeight = objectBounds.maxY - objectBounds.minY
  const areaWidth = areaBounds.maxX - areaBounds.minX
  const areaHeight = areaBounds.maxY - areaBounds.minY

  let deltaX = 0
  let deltaY = 0

  // Se l'oggetto è più grande della print area lungo un asse, non è contenibile
  // via traslazione: lo centriamo su quell'asse per evitare drift sui bordi.
  if (objectWidth > areaWidth + AREA_CONTAINMENT_EPSILON) {
    const areaCenterX = (areaBounds.minX + areaBounds.maxX) / 2
    const objectCenterX = (objectBounds.minX + objectBounds.maxX) / 2
    deltaX = areaCenterX - objectCenterX
  } else if (objectBounds.minX < areaBounds.minX - AREA_CONTAINMENT_EPSILON) {
    deltaX = areaBounds.minX - objectBounds.minX
  } else if (objectBounds.maxX > areaBounds.maxX + AREA_CONTAINMENT_EPSILON) {
    deltaX = areaBounds.maxX - objectBounds.maxX
  }

  if (objectHeight > areaHeight + AREA_CONTAINMENT_EPSILON) {
    const areaCenterY = (areaBounds.minY + areaBounds.maxY) / 2
    const objectCenterY = (objectBounds.minY + objectBounds.maxY) / 2
    deltaY = areaCenterY - objectCenterY
  } else if (objectBounds.minY < areaBounds.minY - AREA_CONTAINMENT_EPSILON) {
    deltaY = areaBounds.minY - objectBounds.minY
  } else if (objectBounds.maxY > areaBounds.maxY + AREA_CONTAINMENT_EPSILON) {
    deltaY = areaBounds.maxY - objectBounds.maxY
  }

  return { deltaX, deltaY }
}

export const getObjectBoundsInAreaSpace = (obj: fabric.Object, area: fabric.Object) => {
  obj.setCoords()
  area.setCoords()

  // Portiamo i vertici di area e oggetto nello spazio locale dell'area di stampa.
  // In questo sistema di riferimento l'area diventa "dritta", quindi possiamo
  // confrontare min/max X,Y anche se area e oggetto sono ruotati nel canvas.
  const areaInverseMatrix = fabric.util.invertTransform(area.calcTransformMatrix())
  const toAreaSpace = (point: fabric.Point) => point.transform(areaInverseMatrix)
  const areaPoints = area.getCoords().map(toAreaSpace)
  const objectPoints = obj.getCoords().map(toAreaSpace)

  return {
    areaBounds: getBoundsFromPoints(areaPoints),
    objectBounds: getBoundsFromPoints(objectPoints),
  }
}

export const translateObjectInAreaSpace = (
  obj: fabric.Object,
  area: fabric.Object,
  dx: number,
  dy: number
) => {
  if (!dx && !dy) return

  // dx/dy sono espressi nello spazio locale dell'area di stampa. Li trasformiamo
  // nel piano del canvas così spostamenti e allineamenti restano corretti anche
  // quando l'area è ruotata.
  const areaMatrix = area.calcTransformMatrix()
  const translatedVector = new fabric.Point(dx, dy).transform(areaMatrix, true)

  obj.set({
    left: obj.left + translatedVector.x,
    top: obj.top + translatedVector.y,
  })
  obj.setCoords()
}

export const setObjectScaleAroundCenter = (
  obj: fabric.Object,
  nextScaleX: number,
  nextScaleY: number,
  action?: string
) => {
  const centerPoint = obj.getCenterPoint()

  obj.set({
    scaleX: nextScaleX,
    scaleY: nextScaleY,
  })
  obj.setPositionByOrigin(centerPoint, 'center', 'center')

  if (TEXT_TYPES.includes(obj.type)) {
    refreshTextObjectDimensions(obj, action)
    return
  }

  obj.setCoords()
}

export const normalizeTextScaleIntoFontSize = (obj: fabric.Object) => {
  if (!TEXT_TYPES.includes(obj.type)) return false

  const textObject = obj as any
  const scaleX = Number.isFinite(obj.scaleX) ? obj.scaleX || 1 : 1
  const scaleY = Number.isFinite(obj.scaleY) ? obj.scaleY || 1 : 1
  const averageScale = (scaleX + scaleY) / 2
  const shouldNormalize =
    Math.abs(scaleX - 1) > AREA_CONTAINMENT_EPSILON ||
    Math.abs(scaleY - 1) > AREA_CONTAINMENT_EPSILON

  if (!shouldNormalize || !Number.isFinite(averageScale) || averageScale <= 0) {
    return false
  }

  const currentFontSize = Number(textObject.fontSize) || 1
  const nextFontSize = Math.max(1, Math.round(currentFontSize * averageScale))
  const centerPoint = obj.getCenterPoint()

  obj.set({
    fontSize: nextFontSize,
    scaleX: 1,
    scaleY: 1,
  })
  obj.setPositionByOrigin(centerPoint, 'center', 'center')
  refreshTextObjectDimensions(obj)

  return true
}

export const getEffectiveTextFontSize = (obj: fabric.Object) => {
  if (!TEXT_TYPES.includes(obj.type)) return Number((obj as any).fontSize) || 1

  const textObject = obj as any
  const baseFontSize = Number(textObject.fontSize) || 1
  const scaleX = Number.isFinite(obj.scaleX) ? obj.scaleX || 1 : 1
  const scaleY = Number.isFinite(obj.scaleY) ? obj.scaleY || 1 : 1
  const averageScale = (scaleX + scaleY) / 2

  if (!Number.isFinite(averageScale) || averageScale <= 0) {
    return Math.max(1, Math.round(baseFontSize))
  }

  return Math.max(1, Math.round(baseFontSize * averageScale))
}

export const shrinkObjectToFitAreaBounds = (
  obj: fabric.Object,
  areaBounds: { minX: number; maxX: number; minY: number; maxY: number },
  objectBounds: { minX: number; maxX: number; minY: number; maxY: number }
) => {
  const objectWidth = objectBounds.maxX - objectBounds.minX
  const objectHeight = objectBounds.maxY - objectBounds.minY
  const areaWidth = areaBounds.maxX - areaBounds.minX
  const areaHeight = areaBounds.maxY - areaBounds.minY

  const mustShrink =
    objectWidth > areaWidth + AREA_CONTAINMENT_EPSILON ||
    objectHeight > areaHeight + AREA_CONTAINMENT_EPSILON

  if (!mustShrink || objectWidth <= 0 || objectHeight <= 0) {
    return false
  }

  const widthShrinkFactor = Math.max(areaWidth - AREA_CONTAINMENT_EPSILON, 0) / objectWidth
  const heightShrinkFactor = Math.max(areaHeight - AREA_CONTAINMENT_EPSILON, 0) / objectHeight
  const shrinkFactor = Math.min(widthShrinkFactor, heightShrinkFactor)

  if (!Number.isFinite(shrinkFactor) || shrinkFactor <= 0 || shrinkFactor >= 1) {
    return false
  }

  setObjectScaleAroundCenter(
    obj,
    (obj.scaleX || 1) * shrinkFactor,
    (obj.scaleY || 1) * shrinkFactor
  )

  return true
}

export const clampRotatingToContainedArea = (
  obj: fabric.Object,
  area: fabric.Object,
  validState: Record<string, any>,
  attemptedState: Record<string, any>
) => {
  applyTransformState(obj, validState)

  let low = 0
  let high = 1
  let bestState = { ...validState }

  // Come nello scaling, cerchiamo per bisezione il massimo avanzamento tra
  // stato valido e stato tentato. Per la rotazione concediamo anche uno
  // shrink uniforme del candidato se la sola traslazione non basta.
  for (let i = 0; i < 14; i += 1) {
    const ratio = (low + high) / 2
    const nextState = interpolateTransformState(obj, area, validState, attemptedState, ratio)

    applyTransformState(obj, nextState)

    let { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area)
    let { deltaX, deltaY } = getContainmentDeltaInAreaSpace(objectBounds, areaBounds)
    translateObjectInAreaSpace(obj, area, deltaX, deltaY)

    if (!isContained(obj, area) && shrinkObjectToFitAreaBounds(obj, areaBounds, objectBounds)) {
      ;({ areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area))
      ;({ deltaX, deltaY } = getContainmentDeltaInAreaSpace(objectBounds, areaBounds))
      translateObjectInAreaSpace(obj, area, deltaX, deltaY)
    }

    if (isContained(obj, area)) {
      bestState = getTransformState(obj)
      low = ratio
    } else {
      high = ratio
    }
  }

  applyTransformState(obj, bestState)

  return bestState
}

// Proviamo prima gli spostamenti più piccoli e vicini all'origine: così durante
// il typing cerchiamo di salvare il fontSize corrente prima di ridurlo davvero.
export const getTextContainmentNudgeCandidates = () =>
  TEXT_CONTAINMENT_NUDGE_STEPS.flatMap((dx) =>
    TEXT_CONTAINMENT_NUDGE_STEPS.map((dy) => ({ dx, dy }))
  ).sort(
    (first, second) =>
      Math.abs(first.dx) + Math.abs(first.dy) - (Math.abs(second.dx) + Math.abs(second.dy))
  )

export const findContainedTextStateWithNudges = (
  obj: fabric.Object,
  area: fabric.Object,
  baseState: Record<string, any>,
  fontSize: number
) => {
  // Per un fontSize candidato tentiamo prima piccoli nudge locali dentro la
  // print area. Se uno funziona, restituiamo direttamente quello stato.
  // Il problema è che il fontSize candidato potrebbe far uscire di veramente poco l'oggetto
  // a causa di approssimazioni sulle coordinate del testo
  // quindi proviamo non solo a ridurre il fontSize ma anche a fare piccoli spostamenti
  const nudgeCandidates = getTextContainmentNudgeCandidates()

  for (const { dx, dy } of nudgeCandidates) {
    applyTransformState(obj, baseState)
    obj.set('fontSize', fontSize)
    refreshTextObjectDimensions(obj, 'typing')

    if (dx || dy) {
      translateObjectInAreaSpace(obj, area, dx, dy)
    }

    if (isContained(obj, area)) {
      return getTransformState(obj)
    }
  }

  return null
}

export const clampTextFontSizeToContainedArea = (obj: fabric.Object, area: fabric.Object) => {
  const attemptedState = getTransformState(obj) as Record<string, any>
  const attemptedFontSize = Math.max(
    1,
    Math.round((obj as any).fontSize || attemptedState.fontSize || 1)
  )

  let low = 1
  let high = attemptedFontSize
  let bestState: Record<string, any> | null = null

  // Ricerca binaria sul fontSize massimo ammesso. Per ogni valore intermedio non
  // testiamo solo il resize puro: proviamo anche piccoli spostamenti compensativi.
  while (low <= high) {
    const mid = Math.floor((low + high) / 2)
    const containedState = findContainedTextStateWithNudges(obj, area, attemptedState, mid)

    if (containedState) {
      bestState = containedState
      low = mid + 1
    } else {
      high = mid - 1
    }
  }

  if (bestState) {
    applyTransformState(obj, bestState)
    return
  }

  applyTransformState(obj, attemptedState)
}

export const getTransformState = (obj: fabric.Object) => {
  const state = {}
  TRANSFORM_PROPS.forEach((prop) => {
    state[prop] = obj[prop]
  })

  return state
}

export const applyTransformState = (obj: fabric.Object, state: Record<string, any>) => {
  const nextState = {}

  TRANSFORM_PROPS.forEach((prop) => {
    if (state[prop] !== undefined) {
      nextState[prop] = state[prop]
    }
  })

  obj.set(nextState)

  // Per il testo Fabric ha bisogno di ricalcolare le dimensioni interne dopo
  // un ripristino/applicazione manuale dello stato, altrimenti il bounding box
  // usato dai controlli e dai vincoli può restare non aggiornato.
  if (TEXT_TYPES.includes(obj.type)) {
    refreshTextObjectDimensions(obj)
    return
  }

  obj.setCoords()
}

export const normalizeAngle = (angle: number) => {
  if (!Number.isFinite(angle)) return 0

  return ((angle % 360) + 360) % 360
}

// Durante il clamp della rotazione usiamo una bisezione tra stato valido e
// stato tentato. Se l'angolo viene interpolato linearmente come un numero,
// vicino a 0/360 si può imboccare il "giro lungo" (es. 1 -> 359 come +358),
// causando forti correzioni di traslazione e quindi lo scatto visibile.
// Qui imponiamo l'interpolazione sull'arco più corto.
export const interpolateAngleShortestPath = (
  startAngle: number,
  endAngle: number,
  ratio: number
) => {
  const normalizedStart = normalizeAngle(startAngle)
  const normalizedEnd = normalizeAngle(endAngle)
  // Delta firmato nel range [-180, 180): evita il "giro lungo" attraversando 0/360.
  const shortestDelta = ((normalizedEnd - normalizedStart + 540) % 360) - 180

  return normalizeAngle(normalizedStart + shortestDelta * ratio)
}

export const setObjectAngleAroundCenter = (obj: fabric.Object, nextAngle: number) => {
  const centerPoint = obj.getCenterPoint()

  obj.set('angle', normalizeAngle(nextAngle))
  obj.setPositionByOrigin(centerPoint, 'center', 'center')
  obj.setCoords()
}

export const getAngleFromCenterToPointer = (center: fabric.Point, pointer: fabric.Point) => {
  if (!center || !pointer) return null

  const dx = pointer.x - center.x
  const dy = pointer.y - center.y

  if (!Number.isFinite(dx) || !Number.isFinite(dy)) return null
  // Se il puntatore è praticamente sul centro, l'angolo è numericamente instabile:
  // evitiamo di aggiornare la rotazione con jitter.
  if (Math.abs(dx) <= AREA_CONTAINMENT_EPSILON && Math.abs(dy) <= AREA_CONTAINMENT_EPSILON) {
    return null
  }

  // In Fabric l'angolo 0 è "verticale" rispetto al controllo rotate, per questo
  // aggiungiamo +90° dopo atan2.
  return normalizeAngle(fabric.util.radiansToDegrees(Math.atan2(dy, dx)) + 90)
}

export const getSnappedAngle = (angle: number, step: number) => {
  if (!Number.isFinite(angle) || !Number.isFinite(step) || step <= 0) {
    return normalizeAngle(angle)
  }

  return normalizeAngle(Math.round(angle / step) * step)
}

export const getAngleDistance = (angleA: number, angleB: number) => {
  const normalizedA = normalizeAngle(angleA)
  const normalizedB = normalizeAngle(angleB)
  const delta = Math.abs(normalizedA - normalizedB)

  return Math.min(delta, 360 - delta)
}

export const getClosestAngleAnchor = (angle: number, anchors: number[]) => {
  let closestAnchor = normalizeAngle(angle)
  let closestDistance = Number.POSITIVE_INFINITY

  anchors.forEach((anchor) => {
    const normalizedAnchor = normalizeAngle(anchor)
    const distance = getAngleDistance(angle, normalizedAnchor)

    if (distance < closestDistance) {
      closestAnchor = normalizedAnchor
      closestDistance = distance
    }
  })

  return {
    angle: closestAnchor,
    distance: closestDistance,
  }
}

export const getMagneticSnapAngle = (angle: number, activeSnapAngle?: number | null) => {
  if (!Number.isFinite(angle)) return null

  // Se l'utente è già entrato nello snap, lo manteniamo finché non si allontana
  // abbastanza dal cardinale corrente. Questo evita tremolii vicino alla soglia.
  if (activeSnapAngle != null && Number.isFinite(activeSnapAngle)) {
    const normalizedActiveAngle = normalizeAngle(activeSnapAngle)

    if (getAngleDistance(angle, normalizedActiveAngle) <= ROTATION_MAGNETIC_SNAP_EXIT_THRESHOLD) {
      return normalizedActiveAngle
    }
  }

  const closestAnchor = getClosestAngleAnchor(angle, ROTATION_MAGNETIC_SNAP_ANGLES)

  if (closestAnchor.distance <= ROTATION_MAGNETIC_SNAP_ENTRY_THRESHOLD) {
    return closestAnchor.angle
  }

  return null
}

export const storeCurrentTransformStateIfValid = (
  obj: fabric.Object | null | undefined,
  area: fabric.Object | null | undefined,
  stateRef: { current: Record<string, any> | null }
) => {
  if (!obj || !area) return

  obj.setCoords()

  if (isContained(obj, area)) {
    stateRef.current = getTransformState(obj)
  }
}

export const isScalingAction = (action: string) => {
  if (!action) return false

  return ['scaling', 'scale', 'scaleX', 'scaleY', 'resizing'].includes(action)
}

export const isRotatingAction = (action: string) => {
  if (!action) return false

  return ['rotating', 'rotate'].includes(action)
}

export const getAreaContactLocks = (
  obj: fabric.Object,
  area: fabric.Object,
  state: Record<string, any>
) => {
  // Fotografiamo quali lati dell'oggetto sono gia' "appoggiati" ai bordi
  // della print area nello stato valido di partenza. Questi latch servono
  // durante la bisezione dello scaling per evitare crescite simmetriche che
  // farebbero uscire subito l'oggetto quando e' gia' a bordo.
  applyTransformState(obj, state)

  const { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area)

  return {
    lockLeft: Math.abs(objectBounds.minX - areaBounds.minX) <= AREA_CONTAINMENT_EPSILON,
    lockRight: Math.abs(objectBounds.maxX - areaBounds.maxX) <= AREA_CONTAINMENT_EPSILON,
    lockTop: Math.abs(objectBounds.minY - areaBounds.minY) <= AREA_CONTAINMENT_EPSILON,
    lockBottom: Math.abs(objectBounds.maxY - areaBounds.maxY) <= AREA_CONTAINMENT_EPSILON,
  }
}

export const interpolateTransformState = (
  obj: fabric.Object,
  area: fabric.Object,
  startState: Record<string, any>,
  endState: Record<string, any>,
  ratio: number,
  options?: { preserveAreaContacts?: boolean }
) => {
  const nextState = {}

  TRANSFORM_PROPS.forEach((prop) => {
    const startValue = startState[prop]
    const endValue = endState[prop]

    // "angle" è periodico e richiede interpolazione circolare; per tutte le
    // altre props numeriche manteniamo l'interpolazione lineare standard.
    if (prop === 'angle' && typeof startValue === 'number' && typeof endValue === 'number') {
      nextState[prop] = interpolateAngleShortestPath(startValue, endValue, ratio)
      return
    }

    if (typeof startValue === 'number' && typeof endValue === 'number') {
      nextState[prop] = startValue + (endValue - startValue) * ratio
      return
    }

    // Le proprietà non numeriche non vengono interpolate: teniamo il valore
    // iniziale finché non raggiungiamo completamente lo stato finale.
    nextState[prop] = ratio < 1 ? startValue : endValue
  })

  if (!options?.preserveAreaContacts) {
    return nextState
  }

  // l'interpolazione standard espande l'oggetto in entrambe le direzioni a partire dal centro,
  // quindi se l'oggetto è già appoggiato a un bordo della print area, rischia di uscire subito anche per piccoli incrementi.
  // Per evitarlo, durante l'interpolazione manteniamo fissi i lati già in contatto con la print area nello stato di partenza e lasciamo crescere nell'altra direzione.
  const { lockLeft, lockRight, lockTop, lockBottom } = getAreaContactLocks(obj, area, startState)

  if (!lockLeft && !lockRight && !lockTop && !lockBottom) {
    return nextState
  }

  applyTransformState(obj, nextState)

  const { areaBounds, objectBounds } = getObjectBoundsInAreaSpace(obj, area)
  let deltaX = 0
  let deltaY = 0

  if (lockLeft !== lockRight) {
    deltaX = lockLeft ? areaBounds.minX - objectBounds.minX : areaBounds.maxX - objectBounds.maxX
  }

  if (lockTop !== lockBottom) {
    deltaY = lockTop ? areaBounds.minY - objectBounds.minY : areaBounds.maxY - objectBounds.maxY
  }

  if (deltaX || deltaY) {
    translateObjectInAreaSpace(obj, area, deltaX, deltaY)
  }

  return getTransformState(obj)
}

export const clampScalingToContainedArea = (
  obj: fabric.Object,
  area: fabric.Object,
  validState: Record<string, any>,
  attemptedState: Record<string, any>
) => {
  // Ripartiamo sempre dall'ultimo stato certamente valido e cerchiamo il massimo
  // avanzamento verso la trasformazione richiesta che rimane ancora contenuto
  // nell'area di stampa.
  applyTransformState(obj, validState)

  let low = 0
  let high = 1
  let bestState = { ...validState }

  // Ricerca binaria tra stato valido (0) e stato tentato (1): in questo modo,
  // se l'utente chiede una dimensione troppo grande, non facciamo revert secco
  // ma troviamo la scala massima ancora ammessa.
  for (let i = 0; i < 14; i += 1) {
    const ratio = (low + high) / 2
    // Per lo scaling non seguiamo soltanto la traiettoria grezza prodotta da
    // Fabric: se l'ultimo stato valido e' gia' aderente a un bordo della print
    // area, manteniamo quell'ancoraggio anche negli stati intermedi.
    const nextState = interpolateTransformState(obj, area, validState, attemptedState, ratio, {
      preserveAreaContacts: true,
    })

    applyTransformState(obj, nextState)

    if (isContained(obj, area)) {
      bestState = getTransformState(obj)
      low = ratio
    } else {
      high = ratio
    }
  }

  applyTransformState(obj, bestState)

  return bestState
}

// Salva/ripristina le coordinate rispetto all'origine dello sfondo e non rispetto al
// canvas assoluto: in questo modo il JSON rimane stabile anche se cambiano viewport
export const translateSerializedObject = (obj: any, dx: number, dy: number) => {
  if (!obj || typeof obj !== 'object') return obj

  const nextObject = { ...obj }

  if (typeof nextObject.left === 'number') nextObject.left += dx
  if (typeof nextObject.top === 'number') nextObject.top += dy
  if (typeof nextObject.x1 === 'number') nextObject.x1 += dx
  if (typeof nextObject.x2 === 'number') nextObject.x2 += dx
  if (typeof nextObject.y1 === 'number') nextObject.y1 += dy
  if (typeof nextObject.y2 === 'number') nextObject.y2 += dy

  if (nextObject.clipPath) {
    nextObject.clipPath = translateSerializedObject(nextObject.clipPath, dx, dy)
  }

  return nextObject
}

export const translateCanvasJson = (canvasJson: any, dx: number, dy: number) => {
  if (!canvasJson || typeof canvasJson !== 'object') return canvasJson

  return {
    ...canvasJson,
    objects: Array.isArray(canvasJson.objects)
      ? canvasJson.objects.map((obj: any) => translateSerializedObject(obj, dx, dy))
      : canvasJson.objects,
  }
}

export const scaleSerializedObjectToArea = (
  obj: any,
  sourceArea: { left: number; top: number; width: number; height: number },
  targetArea: { left: number; top: number; width: number; height: number }
) => {
  if (!obj || typeof obj !== 'object') return obj

  const scaleX = targetArea.width / sourceArea.width
  const scaleY = targetArea.height / sourceArea.height
  const nextObject = { ...obj }
  // Nel JSON Fabric width/height rappresentano la misura base dell'oggetto, mentre
  // la dimensione realmente visibile dipende anche da scaleX/scaleY. Per mantenere
  // coerenti margini e ancoraggi rispetto alla print area dobbiamo quindi partire
  // dalla dimensione "rendered" nello spazio sorgente.
  const objectWidth =
    typeof nextObject.width === 'number' ? nextObject.width * (nextObject.scaleX || 1) : 0
  const objectHeight =
    typeof nextObject.height === 'number' ? nextObject.height * (nextObject.scaleY || 1) : 0

  const sourceRight = sourceArea.left + sourceArea.width
  const sourceBottom = sourceArea.top + sourceArea.height
  const targetRight = targetArea.left + targetArea.width
  const targetBottom = targetArea.top + targetArea.height
  const leftMargin = typeof nextObject.left === 'number' ? nextObject.left - sourceArea.left : 0
  const rightMargin =
    typeof nextObject.left === 'number' ? sourceRight - (nextObject.left + objectWidth) : 0
  const topMargin = typeof nextObject.top === 'number' ? nextObject.top - sourceArea.top : 0
  const bottomMargin =
    typeof nextObject.top === 'number' ? sourceBottom - (nextObject.top + objectHeight) : 0
  const horizontalFill = objectWidth > 0 ? objectWidth / sourceArea.width : 0
  const verticalFill = objectHeight > 0 ? objectHeight / sourceArea.height : 0
  const touchesLeft = Math.abs(leftMargin) <= 1
  const touchesRight = Math.abs(rightMargin) <= 1
  const touchesTop = Math.abs(topMargin) <= 1
  const touchesBottom = Math.abs(bottomMargin) <= 1

  // Manteniamo proporzioni ma scegliamo la scala in base all'asse "vincolante".
  // Esempio: un logo che occupa tutta la larghezza della sorgente deve occupare
  // tutta la larghezza anche nella target, quindi in quel caso usiamo scaleX.
  // Non applichiamo mai scaleX e scaleY separatamente all'oggetto, altrimenti il
  // risultato sarebbe stretchato. La scelta dell'asse serve solo a capire quale
  // rapporto usare come scala uniforme dell'intero oggetto.
  let uniformScale = Math.min(scaleX, scaleY)

  if (touchesLeft && touchesRight) {
    uniformScale = scaleX
  } else if (touchesTop && touchesBottom) {
    uniformScale = scaleY
  } else if (horizontalFill >= verticalFill) {
    uniformScale = scaleX
  } else if (verticalFill > horizontalFill) {
    uniformScale = scaleY
  }

  const scaledObjectWidth = objectWidth * uniformScale
  const scaledObjectHeight = objectHeight * uniformScale

  if (typeof nextObject.left === 'number') {
    // Manteniamo il riferimento geometrico rispetto al bordo "dominante":
    // se l'oggetto era appoggiato a sinistra/destra nella sorgente, resta tale nella target.
    // In caso quasi simmetrico lo trattiamo come centrato.
    // In questo modo un elemento "attaccato" a un bordo non viene ricentrato
    // artificialmente durante copy/paste o sync verso altre posizioni.
    if (Math.abs(leftMargin - rightMargin) <= 1) {
      const sourceCenterX = nextObject.left + objectWidth / 2
      const targetCenterX = targetArea.left + (sourceCenterX - sourceArea.left) * scaleX
      nextObject.left = targetCenterX - scaledObjectWidth / 2
    } else if (leftMargin <= rightMargin) {
      nextObject.left = targetArea.left + leftMargin * scaleX
    } else {
      nextObject.left = targetRight - rightMargin * scaleX - scaledObjectWidth
    }
  }

  if (typeof nextObject.top === 'number') {
    const topMargin = nextObject.top - sourceArea.top
    const bottomMargin = sourceBottom - (nextObject.top + objectHeight)

    // Stessa logica anche sull'asse verticale: top, bottom oppure centro.
    if (Math.abs(topMargin - bottomMargin) <= 1) {
      const sourceCenterY = nextObject.top + objectHeight / 2
      const targetCenterY = targetArea.top + (sourceCenterY - sourceArea.top) * scaleY
      nextObject.top = targetCenterY - scaledObjectHeight / 2
    } else if (topMargin <= bottomMargin) {
      nextObject.top = targetArea.top + topMargin * scaleY
    } else {
      nextObject.top = targetBottom - bottomMargin * scaleY - scaledObjectHeight
    }
  }

  if (typeof nextObject.x1 === 'number') {
    nextObject.x1 = targetArea.left + (nextObject.x1 - sourceArea.left) * scaleX
  }

  if (typeof nextObject.x2 === 'number') {
    nextObject.x2 = targetArea.left + (nextObject.x2 - sourceArea.left) * scaleX
  }

  if (typeof nextObject.y1 === 'number') {
    nextObject.y1 = targetArea.top + (nextObject.y1 - sourceArea.top) * scaleY
  }

  if (typeof nextObject.y2 === 'number') {
    nextObject.y2 = targetArea.top + (nextObject.y2 - sourceArea.top) * scaleY
  }

  if (typeof nextObject.scaleX === 'number') nextObject.scaleX *= uniformScale
  if (typeof nextObject.scaleY === 'number') nextObject.scaleY *= uniformScale

  if (nextObject.clipPath) {
    // Se l'oggetto usa una clipPath serializzata, anche quella va rimappata nella
    // nuova print area; altrimenti dopo il reload la geometria visiva non coincide.
    nextObject.clipPath = scaleSerializedObjectToArea(nextObject.clipPath, sourceArea, targetArea)
  }

  return nextObject
}

export const scaleCanvasJsonToArea = (
  canvasJson: any,
  sourceArea: { left: number; top: number; width: number; height: number } | null,
  targetArea: { left: number; top: number; width: number; height: number } | null
) => {
  if (!canvasJson || !sourceArea || !targetArea) return canvasJson
  if (!sourceArea.width || !sourceArea.height || !targetArea.width || !targetArea.height) {
    return canvasJson
  }

  return {
    ...canvasJson,
    // La riscalatura avviene sul JSON serializzato prima di ricreare gli oggetti Fabric:
    // così la stessa logica vale sia per "incolla stile" sia per sync automatico.
    // Qui convertiamo un layout espresso nella print area sorgente in un layout
    // equivalente espresso nella print area target.
    objects: Array.isArray(canvasJson.objects)
      ? canvasJson.objects.map((obj: any) =>
          scaleSerializedObjectToArea(obj, sourceArea, targetArea)
        )
      : canvasJson.objects,
  }
}
